import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Newspaper, Calendar, CheckCircle2, EyeOff, X, MapPin, Search, Filter } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import DataTable from '../common/DataTable';
import StatusBadge from '../common/StatusBadge';
import Thumbnail from '../common/Thumbnail';
import { Modal, FormField, FileUpload, PrimaryButton, SecondaryButton } from '../common/FormComponents';
import { newsAPI, categoriesAPI, mainCategoriesAPI, locationsAPI } from '../../services/api';
import { NEWS_STATUSES, COLORS, formatLabel } from '../../utils/constants';
import { newsImage } from '../../utils/media';

const C = COLORS;

const NewsPage = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, today: 0, active: 0, inactive: 0 });

  // Filter state
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filter, setFilter] = useState({});
  const [searchInput, setSearchInput] = useState('');

  // Reference lists
  const [mainCategories, setMainCategories] = useState([]);
  const [allSubcategories, setAllSubcategories] = useState([]);
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // Modal & form state
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [files, setFiles] = useState([]);
  const [formCities, setFormCities] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch news articles with pagination and all combined filters
  const fetchData = useCallback(async () => {
    try {
      const params = {
        page,
        limit: itemsPerPage,
        ...filter,
      };
      if (selectedMainCategory) params.main_category_id = selectedMainCategory;
      if (selectedCategory) params.category_id = selectedCategory;
      if (selectedLanguage) params.language = selectedLanguage;
      if (selectedState) params.state_id = selectedState;
      if (selectedCity) params.city_id = selectedCity;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await newsAPI.getAll(params);
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error(err);
    }
  }, [page, itemsPerPage, filter, selectedMainCategory, selectedCategory, selectedLanguage, selectedState, selectedCity, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch counts: total news, today's news, total active, total inactive reflecting active filters
  const fetchStats = useCallback(async () => {
    try {
      const params = { ...filter };
      if (selectedMainCategory) params.main_category_id = selectedMainCategory;
      if (selectedCategory) params.category_id = selectedCategory;
      if (selectedLanguage) params.language = selectedLanguage;
      if (selectedState) params.state_id = selectedState;
      if (selectedCity) params.city_id = selectedCity;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await newsAPI.getStats(params);
      setStats(res.data || { total: 0, today: 0, active: 0, inactive: 0 });
    } catch (err) {
      console.error('Failed to fetch news stats:', err);
    }
  }, [filter, selectedMainCategory, selectedCategory, selectedLanguage, selectedState, selectedCity, startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Load Main Categories, all Subcategories, and States on initial mount
  useEffect(() => {
    mainCategoriesAPI.getAll({ limit: 1000 })
      .then(res => setMainCategories(res.data.data || []))
      .catch(err => console.error('Failed to load main categories:', err));

    categoriesAPI.getAll({ limit: 1000 })
      .then(res => {
        const subs = res.data.data || [];
        setAllSubcategories(subs);
        setFilteredSubcategories(subs);
      })
      .catch(err => console.error('Failed to load subcategories:', err));

    locationsAPI.getStates()
      .then(res => setStates(res.data || []))
      .catch(err => console.error('Failed to load states:', err));
  }, []);

  // Cascading: Subcategories dropdown updates when Main Category changes
  useEffect(() => {
    if (!selectedMainCategory) {
      setFilteredSubcategories(allSubcategories);
      return;
    }
    mainCategoriesAPI.getById(selectedMainCategory)
      .then(res => {
        setFilteredSubcategories(res.data.subcategories || []);
      })
      .catch(err => {
        console.error('Failed to load subcategories for main category:', err);
        setFilteredSubcategories([]);
      });
  }, [selectedMainCategory, allSubcategories]);

  // Cascading: Cities dropdown updates when State changes
  useEffect(() => {
    if (!selectedState) {
      setCities([]);
      setSelectedCity('');
      return;
    }
    locationsAPI.getCities(selectedState)
      .then(res => setCities(res.data || []))
      .catch(err => {
        console.error('Failed to load cities for state:', err);
        setCities([]);
      });
  }, [selectedState]);

  // Active object references
  const activeMainCategoryObj = mainCategories.find(m => String(m.id) === String(selectedMainCategory));
  const activeCategoryObj = allSubcategories.find(c => String(c.id) === String(selectedCategory));
  const activeStateObj = states.find(s => String(s.id) === String(selectedState));
  const activeCityObj = cities.find(c => String(c.id) === String(selectedCity));

  // Filter change handlers
  const handleMainCategoryChange = (e) => {
    setSelectedMainCategory(e.target.value);
    setSelectedCategory('');
    setPage(1);
  };

  const handleSubcategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setPage(1);
  };

  const handleLanguageChange = (e) => {
    setSelectedLanguage(e.target.value);
    setPage(1);
  };

  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
    setSelectedCity('');
    setPage(1);
  };

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
    setPage(1);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setPage(1);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setPage(1);
  };

  const clearDateRange = () => {
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const handleSearchChange = (val) => {
    setSearchInput(val);
    setFilter(p => ({ ...p, search: val.trim() || undefined }));
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setFilter(p => ({ ...p, search: undefined }));
    setPage(1);
  };

  const resetAllFilters = () => {
    setSelectedMainCategory('');
    setSelectedCategory('');
    setSelectedLanguage('');
    setSelectedState('');
    setSelectedCity('');
    setStartDate('');
    setEndDate('');
    setSearchInput('');
    setFilter({});
    setPage(1);
  };

  // Compile list of active filters for chips & summary
  const activeFilters = useMemo(() => {
    const list = [];
    if (selectedMainCategory) {
      list.push({
        key: 'main_category',
        label: `Main: ${activeMainCategoryObj ? activeMainCategoryObj.name : selectedMainCategory}`,
        clear: () => { setSelectedMainCategory(''); setSelectedCategory(''); setPage(1); }
      });
    }
    if (selectedCategory) {
      list.push({
        key: 'category',
        label: `Sub: ${activeCategoryObj ? activeCategoryObj.name : selectedCategory}`,
        clear: () => { setSelectedCategory(''); setPage(1); }
      });
    }
    if (selectedLanguage) {
      list.push({
        key: 'language',
        label: `Lang: ${selectedLanguage === 'hi' ? 'Hindi' : 'English'}`,
        clear: () => { setSelectedLanguage(''); setPage(1); }
      });
    }
    if (selectedState) {
      list.push({
        key: 'state',
        label: `State: ${activeStateObj ? activeStateObj.name : selectedState}`,
        clear: () => { setSelectedState(''); setSelectedCity(''); setPage(1); }
      });
    }
    if (selectedCity) {
      list.push({
        key: 'city',
        label: `City: ${activeCityObj ? activeCityObj.name : selectedCity}`,
        clear: () => { setSelectedCity(''); setPage(1); }
      });
    }
    if (startDate || endDate) {
      list.push({
        key: 'date',
        label: `Date: ${startDate || 'Start'} → ${endDate || 'End'}`,
        clear: clearDateRange
      });
    }
    if (filter.status) {
      list.push({
        key: 'status',
        label: `Status: ${formatLabel(filter.status)}`,
        clear: () => { setFilter(p => ({ ...p, status: undefined })); setPage(1); }
      });
    }
    if (filter.search) {
      list.push({
        key: 'search',
        label: `Search: "${filter.search}"`,
        clear: handleClearSearch
      });
    }
    return list;
  }, [selectedMainCategory, activeMainCategoryObj, selectedCategory, activeCategoryObj, selectedLanguage, selectedState, activeStateObj, selectedCity, activeCityObj, startDate, endDate, filter]);

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  // Remove the stored cover image
  const handleRemoveCover = async () => {
    if (!modal?.id) return;
    if (!window.confirm('Remove this cover image? The file is deleted from the server.')) return;
    setLoading(true);
    try {
      await newsAPI.removeCover(modal.id);
      setForm(p => ({ ...p, _image: null }));
      fetchData();
      fetchStats();
    } catch (err) {
      console.error(err);
      window.alert(err?.response?.data?.error || 'Could not remove the image.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title) return;
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && !k.startsWith('_') && fd.append(k, v));
      if (files[0]) fd.append('cover_image', files[0]);

      if (modal?.id) {
        await newsAPI.update(modal.id, fd);
      } else {
        await newsAPI.create(fd);
      }
      setModal(null);
      setForm({});
      setFiles([]);
      fetchData();
      fetchStats();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await newsAPI.delete(row.id);
      fetchData();
      fetchStats();
    } catch (err) {
      alert('Failed to delete article');
    }
  };

  const openEdit = async (row) => {
    setModal({ id: row.id });
    setFiles([]);
    try {
      const res = await newsAPI.getById(row.id);
      const r = res.data;
      setForm({
        title: r.title,
        description: r.description,
        body: r.body,
        category_id: r.category_id || '',
        status: r.status,
        language: r.language || 'en',
        state_id: r.state_id || '',
        city_id: r.city_id || '',
        _image: newsImage(r)
      });
      if (r.state_id) {
        locationsAPI.getCities(r.state_id).then(cres => setFormCities(cres.data || [])).catch(() => setFormCities([]));
      } else {
        setFormCities([]);
      }
    } catch (err) {
      setForm({
        title: row.title,
        description: row.description,
        category_id: row.category_id || '',
        status: row.status,
        language: row.language || 'en',
        state_id: row.state_id || '',
        city_id: row.city_id || '',
        _image: newsImage(row)
      });
      if (row.state_id) {
        locationsAPI.getCities(row.state_id).then(cres => setFormCities(cres.data || [])).catch(() => setFormCities([]));
      } else {
        setFormCities([]);
      }
    }
  };

  const selectStyle = (isActive) => ({
    height: 40,
    padding: '0 12px',
    border: `1.5px solid ${isActive ? '#4F46E5' : '#CBD5E1'}`,
    borderRadius: 10,
    fontSize: 13,
    fontWeight: isActive ? 600 : 500,
    color: isActive ? '#4F46E5' : '#334155',
    background: isActive ? '#EEF2FF' : '#fff',
    outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
    transition: 'all 0.15s ease',
    minWidth: 140,
  });

  const columns = [
    { key: 'cover_image', label: 'Image', render: (v, row) => <Thumbnail src={newsImage(row)} alt={row.title} /> },
    {
      key: 'title',
      label: 'Title',
      wrap: true,
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        minWidth: 280,
        maxWidth: 520,
      },
      render: (v, row) => (
        <div style={{ maxWidth: 520, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
          <div style={{ fontWeight: 600, color: C.g800, lineHeight: 1.45, display: 'flex', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{v}</span>
            {row.language && (
              <span style={{
                fontSize: 10,
                fontWeight: 700,
                padding: '1px 5px',
                borderRadius: 4,
                background: row.language === 'hi' ? '#FEF3C7' : '#E0E7FF',
                color: row.language === 'hi' ? '#92400E' : '#3730A3',
                textTransform: 'uppercase',
                flexShrink: 0,
                marginTop: 2
              }}>
                {row.language}
              </span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'category_name',
      label: 'Category',
      render: v => v
        ? <span style={{ fontSize: 12, color: C.g500, background: C.g100, padding: '3px 10px', borderRadius: 6 }}>{v}</span>
        : <span style={{ fontSize: 12, color: C.g400 }}>—</span>
    },
    {
      key: 'city_state',
      label: 'City / State',
      render: (v, row) => {
        if (!row.city_name && !row.state_name) {
          return <span style={{ fontSize: 12, color: C.g400 }}>—</span>;
        }
        return (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: C.g700, lineHeight: 1.4 }}>
            <MapPin size={13} color={C.primary || '#4F46E5'} style={{ flexShrink: 0 }} />
            <span>
              {row.city_name ? <strong style={{ color: C.g800, fontWeight: 600 }}>{row.city_name}</strong> : null}
              {row.city_name && row.state_name ? ', ' : ''}
              {row.state_name ? <span style={{ color: C.g600 }}>{row.state_name}</span> : null}
            </span>
          </div>
        );
      }
    },
    {
      key: 'author_name',
      label: 'Author / Source',
      render: (v, r) => v || (r.source_name
        ? <span style={{ fontSize: 12, color: C.g500 }}>{r.source_name}{r.source ? ` · ${r.source}` : ''}</span>
        : <span style={{ color: C.g400 }}>—</span>)
    },
    { key: 'created_at', label: 'Date', render: v => new Date(v).toLocaleDateString() },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
  ];

  return (
    <AppLayout title="News Management">
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.g900 }}>News Articles</h2>
          <span style={{ fontSize: 13, color: C.g500, marginTop: 2, display: 'block' }}>
            Manage news feeds, categories, article content, and publication status
          </span>
        </div>
        <PrimaryButton onClick={() => { setForm({ language: 'en' }); setFiles([]); setModal({}); setFormCities([]); }} icon={<Plus size={18} />}>
          Add Article
        </PrimaryButton>
      </div>

      {/* ─── 1. Search & Filter Bar (Above the boxes) ─── */}
      <div style={{
        background: '#fff',
        borderRadius: 16,
        border: `1px solid ${C.g200}`,
        padding: '16px 20px',
        marginBottom: 20,
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}>
        {/* Row 1: Search & Filter Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Search Input */}
          <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.g400 }} />
            <input
              type="text"
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="Search articles by title, author, source..."
              style={{
                width: '100%',
                padding: '10px 34px 10px 38px',
                border: `1.5px solid ${searchInput ? '#818CF8' : C.g200}`,
                borderRadius: 10,
                fontSize: 14,
                outline: 'none',
                background: searchInput ? '#F5F7FF' : C.g50,
                boxSizing: 'border-box',
                transition: 'all 0.15s ease'
              }}
            />
            {searchInput && (
              <button
                type="button"
                onClick={handleClearSearch}
                title="Clear search"
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: C.g400,
                  cursor: 'pointer',
                  padding: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <X size={15} />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <select
            value={filter.status || ''}
            onChange={e => {
              setFilter(p => ({ ...p, status: e.target.value || undefined }));
              setPage(1);
            }}
            title="Filter by Status"
            style={{ ...selectStyle(!!filter.status), minWidth: 120 }}
          >
            <option value="">All Status</option>
            {NEWS_STATUSES.map(s => (
              <option key={s} value={s}>{formatLabel(s)}</option>
            ))}
          </select>

          {/* Main Category */}
          <select
            value={selectedMainCategory}
            onChange={handleMainCategoryChange}
            title="Filter by Main Category"
            style={selectStyle(!!selectedMainCategory)}
          >
            <option value="">All Main Categories ({mainCategories.length})</option>
            {mainCategories.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>

          {/* Subcategory */}
          <select
            value={selectedCategory}
            onChange={handleSubcategoryChange}
            title="Filter by Subcategory"
            style={selectStyle(!!selectedCategory)}
          >
            <option value="">
              {selectedMainCategory
                ? `All ${activeMainCategoryObj ? activeMainCategoryObj.name : ''} Subcategories (${filteredSubcategories.length})`
                : `All Subcategories (${filteredSubcategories.length})`}
            </option>
            {filteredSubcategories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          {/* Language */}
          <select
            value={selectedLanguage}
            onChange={handleLanguageChange}
            title="Filter by Language"
            style={{ ...selectStyle(!!selectedLanguage), minWidth: 120 }}
          >
            <option value="">All Languages</option>
            <option value="en">English (EN)</option>
            <option value="hi">Hindi (HI)</option>
          </select>

          {/* State */}
          <select
            value={selectedState}
            onChange={handleStateChange}
            title="Filter by State"
            style={selectStyle(!!selectedState)}
          >
            <option value="">All States ({states.length})</option>
            {states.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          {/* City */}
          <select
            value={selectedCity}
            onChange={handleCityChange}
            disabled={!selectedState}
            title={selectedState ? 'Filter by City' : 'Select a State first'}
            style={{
              ...selectStyle(!!selectedCity),
              opacity: selectedState ? 1 : 0.6,
              cursor: selectedState ? 'pointer' : 'not-allowed',
              background: !selectedState ? '#F8FAFC' : selectedCity ? '#EEF2FF' : '#fff'
            }}
          >
            <option value="">
              {selectedState ? `All Cities (${cities.length})` : 'Select State first'}
            </option>
            {cities.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          {/* Date Range */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 40,
            padding: '0 10px',
            border: `1.5px solid ${startDate || endDate ? '#4F46E5' : '#CBD5E1'}`,
            borderRadius: 10,
            background: startDate || endDate ? '#EEF2FF' : '#fff',
            boxSizing: 'border-box'
          }}>
            <Calendar size={14} color={startDate || endDate ? '#4F46E5' : C.g400} />
            <input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              title="Start Date (From)"
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: 12,
                outline: 'none',
                color: startDate ? '#4F46E5' : '#334155',
                fontWeight: startDate ? 600 : 400,
                cursor: 'pointer',
                fontFamily: 'inherit'
              }}
            />
            <span style={{ fontSize: 12, color: C.g400 }}>to</span>
            <input
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              title="End Date (To)"
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: 12,
                outline: 'none',
                color: endDate ? '#4F46E5' : '#334155',
                fontWeight: endDate ? 600 : 400,
                cursor: 'pointer',
                fontFamily: 'inherit'
              }}
            />
            {(startDate || endDate) && (
              <button
                type="button"
                onClick={clearDateRange}
                title="Clear date range"
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: 2,
                  display: 'flex',
                  alignItems: 'center',
                  color: C.g500
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Reset All Filters Button */}
          {activeFilters.length > 0 && (
            <button
              type="button"
              onClick={resetAllFilters}
              title="Reset all filters"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                height: 40,
                padding: '0 14px',
                background: '#FEF2F2',
                border: '1.5px solid #FECACA',
                borderRadius: 10,
                color: '#DC2626',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease'
              }}
            >
              <X size={14} /> Reset ({activeFilters.length})
            </button>
          )}
        </div>

        {/* Row 2: Applied Filter Badges */}
        {activeFilters.length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
            paddingTop: 8,
            borderTop: `1px solid ${C.g100}`
          }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.g400, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Applied Filters:
            </span>
            {activeFilters.map(f => (
              <span
                key={f.key}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '3px 8px',
                  background: '#EEF2FF',
                  border: '1px solid #C7D2FE',
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#4338CA'
                }}
              >
                {f.label}
                <button
                  type="button"
                  onClick={f.clear}
                  title={`Remove filter ${f.label}`}
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    color: '#6366F1'
                  }}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={resetAllFilters}
              style={{
                background: 'none',
                border: 'none',
                color: C.g500,
                fontSize: 11,
                fontWeight: 600,
                textDecoration: 'underline',
                cursor: 'pointer',
                marginLeft: 4,
                padding: 0
              }}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* ─── 2 Metric Stat Boxes (Total News & Today News | Total Active & Total Inactive) ─── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 16,
        marginBottom: 20
      }}>
        {/* Box 1: Total News and Today's News */}
        <div style={{
          background: '#fff',
          borderRadius: 16,
          border: `1.5px solid #C7D2FE`,
          padding: '20px 22px',
          boxShadow: '0 4px 14px rgba(79, 70, 229, 0.05), 0 1px 3px rgba(0,0,0,0.02)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'linear-gradient(90deg, #4F46E5 0%, #818CF8 100%)'
          }} />

          {/* Box Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#4F46E5', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                News Ingestion Overview
              </span>
              <h3 style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 800, color: C.g900 }}>
                Total News & Today
              </h3>
            </div>
            <div style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              background: '#EEF2FF',
              color: '#4F46E5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Newspaper size={20} />
            </div>
          </div>

          {/* Dual Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'flex-start' }}>
            {/* Total News Count */}
            <div style={{ borderRight: `1px solid ${C.g100}`, paddingRight: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.g500, display: 'block' }}>
                Total News
              </span>
              <div style={{ fontSize: 26, fontWeight: 800, color: C.g900, marginTop: 4, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                {stats.total?.toLocaleString() ?? 0}
              </div>
              <span style={{ fontSize: 11, color: C.g400, marginTop: 4, display: 'block' }}>
                {activeFilters.length ? 'Filtered repository' : 'All articles in bank'}
              </span>
            </div>

            {/* Today News Count */}
            <div style={{ paddingLeft: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.g500 }}>
                  Today's News
                </span>
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#15803D',
                  background: '#DCFCE7',
                  padding: '2px 6px',
                  borderRadius: 10,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3
                }}>
                  <Calendar size={10} /> Today
                </span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#16A34A', marginTop: 4, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                +{stats.today?.toLocaleString() ?? 0}
              </div>
              <span style={{ fontSize: 11, color: C.g400, marginTop: 4, display: 'block' }}>
                Published today
              </span>
            </div>
          </div>

          {/* Subtitle / Filter summary */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: 12,
            borderTop: `1px solid ${C.g100}`,
            marginTop: 14
          }}>
            <span style={{ fontSize: 12, color: C.g600, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>
              {activeFilters.length
                ? `Filters (${activeFilters.length}): ${activeFilters.map(f => f.label).join(' · ')}`
                : 'Complete multi-source repository'}
            </span>
            {activeFilters.length > 0 && (
              <button
                type="button"
                onClick={resetAllFilters}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#4F46E5',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: 0
                }}
              >
                Reset <X size={12} />
              </button>
            )}
          </div>
        </div>

        {/* Box 2: Total Active and Total Inactive */}
        <div style={{
          background: '#fff',
          borderRadius: 16,
          border: `1.5px solid #A7F3D0`,
          padding: '20px 22px',
          boxShadow: '0 4px 14px rgba(16, 185, 129, 0.05), 0 1px 3px rgba(0,0,0,0.02)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'linear-gradient(90deg, #10B981 0%, #34D399 100%)'
          }} />

          {/* Box Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#059669', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Article Availability
              </span>
              <h3 style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 800, color: C.g900 }}>
                Total Active & Inactive
              </h3>
            </div>
            <div style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              background: '#ECFDF5',
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CheckCircle2 size={20} />
            </div>
          </div>

          {/* Dual Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'flex-start' }}>
            {/* Total Active Count */}
            <div style={{ borderRight: `1px solid ${C.g100}`, paddingRight: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.g500 }}>
                  Total Active
                </span>
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#059669',
                  background: '#ECFDF5',
                  padding: '2px 6px',
                  borderRadius: 10
                }}>
                  Live
                </span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#059669', marginTop: 4, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                {stats.active?.toLocaleString() ?? 0}
              </div>
              <span style={{ fontSize: 11, color: C.g400, marginTop: 4, display: 'block' }}>
                Published & available in app
              </span>
            </div>

            {/* Total Inactive Count */}
            <div style={{ paddingLeft: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.g500 }}>
                  Total Inactive
                </span>
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.g600,
                  background: C.g100,
                  padding: '2px 6px',
                  borderRadius: 10,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3
                }}>
                  <EyeOff size={10} /> Inactive
                </span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: C.g700, marginTop: 4, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                {stats.inactive?.toLocaleString() ?? 0}
              </div>
              <span style={{ fontSize: 11, color: C.g400, marginTop: 4, display: 'block' }}>
                Draft & review status
              </span>
            </div>
          </div>

          {/* Subtitle / Health Note */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: 12,
            borderTop: `1px solid ${C.g100}`,
            marginTop: 14
          }}>
            <span style={{ fontSize: 12, color: C.g600, fontWeight: 500 }}>
              {stats.total > 0
                ? `${Math.round(((stats.active || 0) / stats.total) * 100)}% articles active and visible`
                : 'Status synchronization healthy'}
            </span>
            <span style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#059669',
              background: '#ECFDF5',
              padding: '2px 8px',
              borderRadius: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4
            }}>
              ● Live Feed
            </span>
          </div>
        </div>
      </div>

      {/* ─── 3. News Articles Table ─── */}
      <DataTable
        hideToolbar={true}
        columns={columns}
        data={data}
        page={page}
        totalPages={totalPages}
        total={total}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={(newLimit) => {
          setItemsPerPage(newLimit);
          setPage(1);
        }}
        onPageChange={setPage}
        onEdit={openEdit}
        onDelete={handleDelete}
        expandRow={row => (
          <div style={{ display: 'flex', gap: 16 }}>
            <Thumbnail src={newsImage(row)} alt={row.title} width={140} height={90} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ margin: 0, fontSize: 13, color: C.g600, lineHeight: 1.6 }}>{row.description || 'No description available.'}</p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8, fontSize: 12, color: C.g500 }}>
                {row.category_name && <span><strong>Category:</strong> {row.category_name}</span>}
                {row.language && <span><strong>Language:</strong> {row.language === 'hi' ? 'Hindi' : 'English'}</span>}
                {(row.state_name || row.city_name) && (
                  <span><strong>Location:</strong> {row.city_name ? `${row.city_name}, ` : ''}{row.state_name}</span>
                )}
                {row.published_at && <span><strong>Published:</strong> {new Date(row.published_at).toLocaleString()}</span>}
              </div>
              {row.keywords && <p style={{ margin: '6px 0 0', fontSize: 12, color: C.g400 }}>Keywords: {row.keywords}</p>}
              {row.link && <a href={row.link} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8, fontSize: 13, color: C.primary, fontWeight: 600, textDecoration: 'none' }}>Read original →</a>}
            </div>
          </div>
        )}
      />

      {modal && (
        <Modal title={modal.id ? 'Edit Article' : 'Add Article'} onClose={() => setModal(null)} wide>
          <FormField label="Title" name="title" value={form.title} onChange={handleChange} required />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField
              label="Category (Subcategory)"
              name="category_id"
              value={form.category_id}
              onChange={handleChange}
              options={allSubcategories.map(c => ({ value: c.id, label: c.name }))}
              placeholder="Select category..."
            />
            <FormField
              label="Language"
              name="language"
              value={form.language || 'en'}
              onChange={handleChange}
              options={[
                { value: 'en', label: 'English' },
                { value: 'hi', label: 'Hindi' }
              ]}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField
              label="State"
              name="state_id"
              value={form.state_id}
              onChange={(e) => {
                const sid = e.target.value;
                setForm(p => ({ ...p, state_id: sid, city_id: '' }));
                if (sid) {
                  locationsAPI.getCities(sid).then(res => setFormCities(res.data || [])).catch(() => setFormCities([]));
                } else {
                  setFormCities([]);
                }
              }}
              options={states.map(s => ({ value: s.id, label: s.name }))}
              placeholder="Select state..."
            />
            <FormField
              label="City"
              name="city_id"
              value={form.city_id}
              onChange={handleChange}
              options={formCities.map(c => ({ value: c.id, label: c.name }))}
              placeholder={form.state_id ? 'Select city...' : 'Select a state first'}
            />
          </div>
          <FormField label="Description / Summary" name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Short summary (shown as the card subtitle)..." />
          <FormField label="Body" name="body" value={form.body} onChange={handleChange} rows={8} placeholder="Full article body (shown in the mobile app)..." />
          <FormField label="Status" name="status" value={form.status} onChange={handleChange} options={NEWS_STATUSES} />
          {form._image && !files.length && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.g700 }}>Current Cover</label>
              <div style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
                <Thumbnail src={form._image} alt={form.title} width={160} height={100} radius={10} />
                {modal?.id && (
                  <button
                    type="button"
                    onClick={handleRemoveCover}
                    disabled={loading}
                    title="Remove image"
                    aria-label="Remove image"
                    style={{
                      position: 'absolute', top: -8, right: -8, width: 26, height: 26,
                      borderRadius: '50%', border: 'none', cursor: loading ? 'default' : 'pointer',
                      background: C.danger, color: '#fff', fontSize: 15, lineHeight: '26px',
                      padding: 0, boxShadow: '0 1px 4px rgba(0,0,0,.3)', opacity: loading ? 0.6 : 1,
                    }}
                  >
                    &times;
                  </button>
                )}
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: C.g400 }}>
                Uploading a new image replaces it, or use &times; to remove it.
              </p>
            </div>
          )}
          <FileUpload label="Cover Image" files={files} onChange={f => setFiles(Array.from(f))} onRemove={() => setFiles([])} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <SecondaryButton onClick={() => setModal(null)}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleSubmit} disabled={loading}>{loading ? 'Saving...' : 'Save'}</PrimaryButton>
          </div>
        </Modal>
      )}
    </AppLayout>
  );
};

export default NewsPage;
